/**
 * Ported from the-answer-2 (BACKLOG §16: lift the components, not the app).
 * Behaviour is theirs; the edges are savvy's. Changes made on the way in:
 *   - the field's identity is a stable string `id`, not a client-assigned
 *     `callTypeFormFieldId` number sitting beside an unrelated `id`;
 *   - a section is a flat `group` name, not a nested object with its own id;
 *   - drafts, feature announcements, router state and toasts are dropped —
 *     they belong to the host app, not the component.
 */

import { useSortable } from "@dnd-kit/sortable";
import React from "react";
import { CSS } from "@dnd-kit/utilities";
import type { FormField } from "./types";
import { GripVertical, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export const SortableElementOption = ({
  option,
  element,
  index,
  onRemoveOption,
  onUpdateOption,
}: {
  option: string;
  element: FormField;
  index: number;
  onRemoveOption: (index: number) => void;
  onUpdateOption: (index: number, value: string) => void;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `${index}-${option}-${element.id}`,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const [localOptionValue, setLocalOptionValue] = React.useState(option);
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 group"
    >
      <div
        className="p-1 opacity-60 group-hover:opacity-100"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4 text-muted-foreground" />
      </div>
      <Input
        id={`element-option-${element.id}-${index}`}
        type="text"
        value={localOptionValue}
        onChange={(e) => {
          setLocalOptionValue(e.target.value);
        }}
        onBlur={() => onUpdateOption(index, localOptionValue)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            onUpdateOption(index, localOptionValue);
            // refocus the input
            e.currentTarget.blur();

            e.currentTarget.focus();
          }
        }}
        className="flex-1"
        aria-label={`Option ${index + 1}`}
      />
      <Button
        type="button"
        variant="destructive"
        size={"icon-xs"}
        className="rounded-md"
        aria-label={`Remove option ${index + 1}`}
        onClick={() => onRemoveOption(index)}
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
};
